import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE_URL } from "../../ApiConfig";
import style from '../../../css/admin/Workshopupload.module.css';

import debounce from "lodash.debounce";
import { fetch_admin_screener_stocks, getSecureToken } from "../../ApisService/apiService";
import { showCustomAlert } from "../../customalert";

const ScreenerData = () => {
  const [screenerData, setScreenerData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStrategy, setSelectedStrategy] = useState(""); 
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);

  useEffect(() => {
    fetchScreenerData(selectedStrategy, page);
  }, [selectedStrategy, page]); 

  const fetchScreenerData = debounce(async (strategy, page) => {
    setLoading(true);
    try {
      const response = await fetch_admin_screener_stocks(strategy, page); 
      if (response.length === 0) {
        setHasMore(false);
      } else {
        setScreenerData((prev) => (page === 1 ? response : [...prev, ...response]));
      }
    } catch (error) {
      console.error("Error fetching screener data:", error);
    }
    setLoading(false);
  }, 500);

  const deleteScreenerItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
          const { secrettoken, ts } = await getSecureToken();
    
    try {
     await axios.post(`${API_BASE_URL}Screener/delete_screener.php`, { id }, {
        headers: {
            'X-TOKEN': secrettoken,
            'X-TIMESTAMP': ts,
          },
      withCredentials: true, // added here
    });
      setScreenerData((prev) => prev.filter((item) => item.id !== id));
        closeModal();
    } catch (error) {
      console.error("Error deleting screener data:", error);
    }
  };

  const handleSearch = debounce((query) => {
    setSearchQuery(query);
  }, 300);

  const handleStrategyChange = (strategy) => {
    setSelectedStrategy(strategy);
    setPage(1); // Reset to first page on strategy change
    setScreenerData([]); // Clear existing data
  };

  // Get unique strategies for dropdown (from API or static list)
  const uniqueStrategies = [...new Set(screenerData.map((item) => item.strategy))];

  useEffect(() => {
    if (!hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prev) => prev + 1);
      }
    }, { root: null, rootMargin: "200px", threshold: 0.5 });

    if (observerRef.current) observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [screenerData.length, hasMore]);


  const strategyList = [
    "VPC SETUP",
    "ROCKET BASE",
    "IPO BASE",
    "FNO SETUP"
  ];
  const [selectedRow, setSelectedRow] = useState(null);
  const [tradeResult, setTradeResult] = useState("");
  const [percentage, setPercentage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const openModal = (row) => {
    setSelectedRow(row);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTradeResult("");
    setPercentage("");
  };

  const handleSubmit = async () => {
          const { secrettoken, ts } = await getSecureToken();
    
    try {
      await axios.post(`${API_BASE_URL}Screener/update_trade_result.php`, {
        code: selectedRow.code,
        trade_result: tradeResult,
        percentage: percentage,
      },{
          headers: {
            'X-TOKEN': secrettoken,
            'X-TIMESTAMP': ts,
          },
        withCredentials: true,
      });

      showCustomAlert("Data saved successfully!");
      closeModal();
    } catch (error) {
      console.error("Error saving trade data:", error);
    }
  };

  return (
    <div className={style.screenercontainer}>
      <h2 className={style.heading}>Screener Data</h2>

      {/* Search & Strategy Filter */}
      <div className={style.controls}>
        <input
          type="text"
          placeholder="Search company or code..."
          className={style.searchBar}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <select className={style.strategyFilter} onChange={(e) => handleStrategyChange(e.target.value)} value={selectedStrategy}>
          <option value="">All Strategies</option>
          {strategyList.map((strategy, index) => (
            <option key={index} value={strategy}>{strategy}</option>
          ))}
        </select>

      </div>

      {loading && <p className={style.loading}>Loading data...</p>}

      <AnimatePresence>
        <div className={style.tableContainer}>
          <table className={style.stable}>
            <thead>
              <tr className={style.tableHeader}>
                <th>Name</th>
                <th>Code</th>
                <th>Symbol</th>
                <th>Strategy</th>
                <th>Industry</th>
                <th>Upload Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {screenerData.length > 0 ? (
                screenerData
                  .filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.code.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((item, index) => {
                    const isLastItem = index === screenerData.length - 1;
                    return (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={style.stableRow}
                        ref={isLastItem ? observerRef : null}
                        onClick={() => openModal(item)}
                      >
                        <td className={style.stableCell} title={item.name}>{item.name.length > 15 ? `${item.name.substring(0, 35)}...` : item.name}</td>
                        <td className={style.stableCell}>{item.code}</td>
                        <td className={style.stableCell}>{item.symbol}</td>
                        <td className={style.stableCell}>{item.strategy}</td>
                        <td className={style.stableCell}>{item.industry || "N/A"}</td>
                        <td className={style.stableCell}>{item.created_at}</td>
                        <td className={style.stableCell}>
                          <button className={style.deleteButton} onClick={() => deleteScreenerItem(item.id)}>❌ Delete</button>
                        </td>
                      </motion.tr>
                    );
                  })
              ) : (
                <tr>
                  <td colSpan="7" className={style.noResults}>No results found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </AnimatePresence>


      {hasMore && !loading && <div className={style.loadMore}>Loading more...</div>}


      {showModal && (
        <div className={style.modalOverlay}>
          <div className={style.modalContent}>
            <h3>Enter Trade Result</h3>
            <p><b>Name:</b> {selectedRow.name}</p>
            <p><b>Code:</b> {selectedRow.code}</p>
            <p><b>Upload date:</b> {selectedRow.created_at}</p>

            <label><b>Trade Result:</b></label>
            <select value={tradeResult} onChange={(e) => setTradeResult(e.target.value)}>
              <option value="">Select</option>
              <option value="Profit">Profit</option>
              <option value="Loss">Loss</option>
            </select>

            <input type="number" placeholder="Percentage" value={percentage} onChange={(e) => setPercentage(e.target.value)} />
       <div className="d-flex justify-content-between">

            <button onClick={handleSubmit}>Save</button>
            <button onClick={closeModal}>Cancel</button>
       </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ScreenerData;
